import React, { useState } from 'react';
import { NEUTRAL, STATUS_COLORS } from './designTokens';

const DAY_NAMES = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];

const DEFAULT_FORM = {
  day_of_week: 1,
  start_time: '09:00',
  end_time: '17:00',
  slot_duration: 60,
  break_duration: 0,
};

function BookingScheduleView({ theme, schedules, addScheduleDay, toggleScheduleDay, deleteScheduleDay }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const ok = await addScheduleDay({
      day_of_week: Number(form.day_of_week),
      start_time: form.start_time,
      end_time: form.end_time,
      slot_duration: Number(form.slot_duration),
      break_duration: Number(form.break_duration),
    });
    setSaving(false);
    if (ok) {
      setShowForm(false);
      setForm(DEFAULT_FORM);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setForm(DEFAULT_FORM);
  };

  return (
    <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${theme.card_border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: NEUTRAL.ink, fontFamily: 'var(--font-heading)' }}>Veckoschema</h2>
          <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>Tider som kunder kan boka</span>
        </div>
        {!showForm && (
          <button type="button" onClick={() => setShowForm(true)} style={{ background: 'none', border: 'none', color: theme.accent_text, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>+ Lägg till dag</button>
        )}
      </div>

      <div style={{ padding: showForm ? '16px 20px' : 0, borderBottom: showForm ? `1px solid ${theme.card_border}` : 'none' }}>
        <p style={{ color: theme.text_secondary, fontSize: '0.875em', marginTop: 0, marginBottom: '16px', padding: showForm ? 0 : '0 20px' }}>
          Ställ in dina återkommande arbetstider. Kunder kan boka lediga tider baserat på detta schema.
        </p>

        {showForm && (
          <form
            onSubmit={handleSubmit}
            style={{ border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, padding: '18px', marginBottom: '20px', background: theme.card_bg }}
          >
            <p style={{ margin: '0 0 14px 0', fontSize: '0.85em', color: theme.text_secondary }}>
              Detta är ditt återkommande veckoschema. Tillfälliga avvikelser och varierande veckor kan hanteras senare.
            </p>

            <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9em' }}>
                Veckodag
                <select
                  value={form.day_of_week}
                  onChange={e => setForm(f => ({ ...f, day_of_week: e.target.value }))}
                  style={{ padding: '6px', borderRadius: theme.btn_radius, border: `1px solid ${theme.card_border}` }}
                >
                  {DAY_NAMES.map((name, i) => (
                    <option key={i} value={i}>{name}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9em' }}>
                Starttid
                <input
                  type="time"
                  value={form.start_time}
                  onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                  style={{ padding: '6px', borderRadius: theme.btn_radius, border: `1px solid ${theme.card_border}` }}
                  required
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9em' }}>
                Sluttid
                <input
                  type="time"
                  value={form.end_time}
                  onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                  style={{ padding: '6px', borderRadius: theme.btn_radius, border: `1px solid ${theme.card_border}` }}
                  required
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9em' }}>
                Slottlängd (min)
                <input
                  type="number"
                  min="5"
                  max="480"
                  value={form.slot_duration}
                  onChange={e => setForm(f => ({ ...f, slot_duration: e.target.value }))}
                  style={{ padding: '6px', borderRadius: theme.btn_radius, border: `1px solid ${theme.card_border}` }}
                  required
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9em' }}>
                Paus (min)
                <input
                  type="number"
                  min="0"
                  max="120"
                  value={form.break_duration}
                  onChange={e => setForm(f => ({ ...f, break_duration: e.target.value }))}
                  style={{ padding: '6px', borderRadius: theme.btn_radius, border: `1px solid ${theme.card_border}` }}
                  required
                />
              </label>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                disabled={saving}
                style={{ background: theme.accent, color: 'white', border: 'none', padding: '8px 18px', borderRadius: theme.btn_radius, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '500' }}
              >
                {saving ? 'Sparar...' : 'Spara schema'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                style={{ background: 'none', color: NEUTRAL.text, border: `1px solid ${theme.card_border}`, padding: '8px 18px', borderRadius: theme.btn_radius, cursor: 'pointer' }}
              >
                Avbryt
              </button>
            </div>
          </form>
        )}

      </div>

      {/* Rader mån–sön */}
      {[1, 2, 3, 4, 5, 6, 0].map((dow, idx) => {
        const s = schedules.find(x => x.day_of_week === dow);
        const last = idx === 6;
        const rowBase = { display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 20px', borderBottom: last ? 'none' : `1px solid ${theme.card_border}` };
        if (!s) {
          return (
            <div key={dow} style={{ ...rowBase, opacity: 0.6 }}>
              <div style={{ width: '44px', fontSize: '13px', fontWeight: '600', color: NEUTRAL.ink }}>{DAY_NAMES[dow].slice(0, 3)}</div>
              <div style={{ flex: 1, fontSize: '13px', color: NEUTRAL.faint }}>Ingen arbetstid</div>
              <button type="button" onClick={() => { setForm({ ...DEFAULT_FORM, day_of_week: dow }); setShowForm(true); }} style={{ background: 'none', border: 'none', color: theme.accent_text, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>+ Lägg till</button>
            </div>
          );
        }
        return (
          <div key={s.id} style={{ ...rowBase, opacity: s.is_active ? 1 : 0.7 }}>
            <div style={{ width: '44px', fontSize: '13px', fontWeight: '600', color: NEUTRAL.ink }}>{DAY_NAMES[dow].slice(0, 3)}</div>
            <div style={{ flex: 1, minWidth: 0, fontSize: '13px', color: NEUTRAL.ink }}>
              {s.start_time?.slice(0, 5)}–{s.end_time?.slice(0, 5)}
              <span style={{ color: NEUTRAL.muted }}> · {s.slot_duration ? `${s.slot_duration} min` : 'tid'} · paus {s.break_duration || 0} min</span>
              {!s.is_active && <span style={{ color: STATUS_COLORS.warning }}> · pausad</span>}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: NEUTRAL.muted, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={!!s.is_active} onChange={() => toggleScheduleDay(s.id, s.is_active)} style={{ accentColor: theme.accent }} /> Aktiv
            </label>
            {deletingScheduleId === s.id ? (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button type="button" onClick={() => { deleteScheduleDay(s.id); setDeletingScheduleId(null); }} style={{ background: STATUS_COLORS.error_bg, border: `1px solid ${STATUS_COLORS.error}`, color: '#B91C1C', padding: '5px 10px', borderRadius: theme.btn_radius, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>Ja, ta bort</button>
                <button type="button" onClick={() => setDeletingScheduleId(null)} style={{ background: 'none', border: `1px solid ${theme.card_border}`, color: NEUTRAL.muted, padding: '5px 10px', borderRadius: theme.btn_radius, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Avbryt</button>
              </div>
            ) : (
              <button type="button" onClick={() => setDeletingScheduleId(s.id)} style={{ background: 'none', border: 'none', color: NEUTRAL.muted, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Ta bort</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default BookingScheduleView;
